import { ChevronLeft, ChevronRight, Flag, LayoutGrid, Clock, X, LogOut } from "lucide-react";
import { QuestionStem } from "./QuestionStem";
import { OptionList } from "./OptionList";

const fmt = (secs) => {
  if (secs == null) return "--:--:--";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const NavButtons = ({ prev, next, prevDisabled, nextDisabled }) => (
  <div className="flex items-center justify-end gap-2 mt-4">
    <button data-testid="prev-question-btn" onClick={prev} disabled={prevDisabled}
      title="Previous question"
      className="flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">
      <ChevronLeft className="w-4 h-4" /> Previous
    </button>
    <button data-testid="next-question-btn" onClick={next} disabled={nextDisabled}
      title="Next question (Enter also advances once an answer is selected)"
      className="flex items-center gap-1 text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-40 transition-colors">
      Next <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

export const TestingView = ({ es, q, pos, timeLeft, walk, walkIndex, setAnswer, toggleMark, setHighlights, toggleStrike, next, prev, openReview, exitWalk, exit }) => {
  const posKey = String(pos);
  const isMarked = es.marked.includes(pos);
  const timerCls =
    timeLeft != null && timeLeft <= 300
      ? "text-rose-600 animate-pulse"
      : timeLeft != null && timeLeft <= 1800
      ? "text-amber-500"
      : "text-slate-800";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="testing-view">
      <header className="fixed top-0 left-0 right-0 h-10 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-3 sm:px-5 shadow-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 hidden sm:inline">
            Exam {es.exam_number}
          </span>
          <span data-testid="question-counter" className="text-sm font-medium text-slate-900 truncate">
            Question {pos} of {es.total_questions}
          </span>
          <span data-testid="section-chip" className="text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full hidden sm:inline">
            Section {es.section || 1} of {es.sections_total || 3}
          </span>
          {isMarked && (
            <Flag className="w-3.5 h-3.5 fill-amber-400 text-amber-500" title="Marked for review" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="exam-timer" className={`flex items-center gap-1.5 font-mono text-sm font-semibold bg-slate-100 px-2.5 py-1 rounded-md ${timerCls}`} aria-live="polite">
            <Clock className="w-3.5 h-3.5" strokeWidth={2} />
            {fmt(timeLeft)}
          </div>
          <button data-testid="exit-exam-btn" onClick={exit}
            title="Exit exam — your progress is saved and the timer keeps running; resume from the dashboard"
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-13 pb-16" style={{ paddingTop: "3.25rem" }}>
        {walk && (
          <div className="mb-2 flex items-center gap-2">
            <span data-testid="walkthrough-chip" className="inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
              {walk.label} — {walkIndex + 1} of {walk.list.length}
              <button data-testid="exit-walkthrough-btn" onClick={exitWalk} className="hover:text-blue-900 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        )}
        {!q ? (
          <div className="animate-pulse space-y-4 mt-4">
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-24 bg-slate-200 rounded" />
          </div>
        ) : (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-1.5">{q.domain}</div>
            <QuestionStem
              stem={q.stem}
              imageUrl={q.image_url}
              highlights={es.highlights?.[posKey] || []}
              onChange={setHighlights}
            />
            <OptionList
              options={q.options}
              selected={es.answers?.[posKey]}
              struck={es.strikeouts?.[posKey] || []}
              onSelect={setAnswer}
              onStrike={toggleStrike}
            />
            <NavButtons
              prev={prev}
              next={next}
              prevDisabled={walk ? walkIndex <= 0 : pos <= (es.section_start || 1)}
              nextDisabled={!walk && pos >= (es.section_end || es.total_questions)}
            />
          </>
        )}
      </main>

      <footer className="fixed bottom-0 left-0 right-0 h-11 bg-white border-t border-slate-200 z-50 flex items-center px-3 sm:px-5 gap-2">
        <button
          data-testid="review-screen-btn"
          onClick={openReview}
          title="Review Screen — see every question's status at a glance and jump anywhere"
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <LayoutGrid className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Review Screen</span><span className="sm:hidden">Review</span>
        </button>
        <button
          data-testid="mark-review-btn"
          onClick={toggleMark}
          title="Mark this question to revisit it before submitting"
          className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
            isMarked
              ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
              : "border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          <Flag className={`w-3.5 h-3.5 ${isMarked ? "fill-amber-400 text-amber-500" : ""}`} />
          <span className="hidden sm:inline">{isMarked ? "Unmark" : "Mark for Review"}</span><span className="sm:hidden">Mark</span>
        </button>
      </footer>
    </div>
  );
};
